import { useState } from 'react';
import type { SubmitHandler} from 'react-hook-form';
import { useForm } from 'react-hook-form';

import type {
  useModalController} from '@uikit';
import {
  Button,
  Modal,
  MODAL_ANIMATION_DURATION,
  ModalCloseButton,
  ModalContent,
  ModalHeading,
  Spinner,
  Textarea
} from '@uikit';

import type { ReportType } from '@api';

import { AngleRight } from '@uikit/icons/AngleRight';
import { Checked2 } from '@uikit/icons/Checked2';

import displayErrorToast from '@shared/helpers/displayErrorToast';

interface ReportCommentFields {
  comment: string;
}

export interface ReportModalProps {
  isReporting: boolean;
  isLoadingReportTypes: boolean;
  isSuccess: boolean;
  modalController: ReturnType<typeof useModalController>;
  modalHeading: string;
  reportCallback: (props: { reportType: ReportType; comment: string }) => void;
  reportTypes: ReportType[];
  resetStates?: () => void;
}

const MAX_COMMENT_LENGTH = 400;

export default function ReportModal({
  isReporting,
  isLoadingReportTypes,
  isSuccess,
  modalController,
  modalHeading = 'Report',
  reportCallback,
  reportTypes,
  resetStates,
}: ReportModalProps) {
  const [selectedReportType, setSelectedReportType] = useState<ReportType | null>(null);
  const {
    register,
    handleSubmit,
    reset: resetForm,
    formState: { errors },
  } = useForm<ReportCommentFields>();

  const onSubmit: SubmitHandler<ReportCommentFields> = async (data) => {
    try {
      if (!selectedReportType) {
        throw new Error('Please select a report type');
      }

      await reportCallback({
        comment: data.comment,
        reportType: selectedReportType,
      });
    } catch (error) {
      displayErrorToast({ error });
    }
  };

  return (
    <Modal
      {...modalController.props}
      onOpenChange={(value) => {
        if (!value) {
          setTimeout(() => {
            resetForm();
            resetStates?.();
            setSelectedReportType(null);
          }, MODAL_ANIMATION_DURATION);
        }

        modalController.props.onOpenChange(value);
      }}
    >
      <ModalContent className="max-w-[470px] w-full flex flex-col items-center p-0">
        <ModalCloseButton className="!m-4" />

        <div className="w-full flex flex-col items-center pt-0 px-4 sm:px-8 pb-6 sm:pb-12">
          {isSuccess && (
            <div className="text-neutral-50 w-[72px] aspect-square flex-shrink-0 bg-green-600 rounded-full mb-4">
              <Checked2 />
            </div>
          )}

          <ModalHeading>
            {isSuccess
              ? 'Report submitted successfully.'
              : selectedReportType
              ? 'Your Comment'
              : modalHeading}
          </ModalHeading>

          {!isSuccess && !selectedReportType && isLoadingReportTypes && (
            <>
              <Spinner className="my-10" />
              <h4 className="text-neutral-400 text-sm text-center">Loading report types...</h4>
            </>
          )}

          {!isSuccess && !selectedReportType && !isLoadingReportTypes && (
            <ul className="flex flex-col w-full">
              {!reportTypes.length && (
                <li>
                  <h4 className="text-neutral-400 text-sm text-center">No report types found</h4>
                </li>
              )}

              {reportTypes.map((reportType) => (
                <li
                  key={reportType.id}
                  className="flex justify-between items-center gap-2 transition rounded py-2 px-4 cursor-pointer hover:bg-primary-100"
                  onClick={() => setSelectedReportType(reportType)}
                >
                  <div className="flex flex-col gap-1">
                    <h4 className="text-neutral-600 text-sm font-bold">{reportType.title}</h4>
                    <p className="text-neutral-400 text-sm">{reportType.description}</p>
                  </div>

                  <div className="w-6 h-6 flex-shrink-0 text-primary-600">
                    <AngleRight />
                  </div>
                </li>
              ))}
            </ul>
          )}

          {!isSuccess && !!selectedReportType && (
            <form onSubmit={handleSubmit(onSubmit)} className="w-full">
              <div className="flex flex-col gap-1 mb-4 bg-secondary-100 rounded-md p-2">
                <div className="flex justify-between">
                  <h4 className="text-neutral-600 text-sm font-bold">{selectedReportType.title}</h4>
                  <button
                    className="button button-sm bg-transparent text-secondary-700 px-2"
                    onClick={() => setSelectedReportType(null)}
                  >
                    Change
                  </button>
                </div>
                <p className="text-neutral-400 text-sm">{selectedReportType.description}</p>
              </div>

              <Textarea
                rows={4}
                placeholder="Leave a comment"
                showLetterCount
                maxLength={MAX_COMMENT_LENGTH}
                {...register('comment', {
                  required: 'This field is required',
                  maxLength: {
                    value: MAX_COMMENT_LENGTH,
                    message: `Comment should be less than ${MAX_COMMENT_LENGTH} symbols`,
                  },
                })}
              />
              {errors.comment && <p className="text-red-500 text-sm">{errors.comment.message}</p>}

              <Button
                className="w-full mt-8"
                variant="secondary"
                type="submit"
                disabled={isReporting}
              >
                {isReporting ? <Spinner size="sm" color="light" /> : 'Submit'}
              </Button>
            </form>
          )}

          {isSuccess && (
            <Button className="w-full mt-2" variant="secondary" onClick={modalController.close}>
              OK
            </Button>
          )}
        </div>
      </ModalContent>
    </Modal>
  );
}
